import { createSelector } from '@reduxjs/toolkit'
import { useEffect, useMemo, useState } from 'react'
import { RootState } from '../../state/store'
import { useAppSelector } from '../../state/hooks'
import { isConfirmedNotReadyToComplete } from '../../functions/sale'
import { useSaleManager, useTrainingModeManager } from '../../ng-compat/hooks'
import {
  selectIsAReturnWithoutPermission,
  selectIsFinalisingSale,
} from '../../state/slices/uiStateSlice'
import {
  selectFulfilmentNoteIsInvalid,
  selectNoteIsInvalid,
  selectUnknownLineItems,
} from '../../state/slices/currentSale/currentSaleSlice'

const selectHasActiveSale = createSelector(
  [(state: RootState) => state.posState.activeSaleLocalId],
  activeSaleLocalId => Boolean(activeSaleLocalId)
)

const selectIsConfirmedNotReadyToComplete = createSelector(
  [
    (state: RootState) => state.currentSale.isSubmittedSaleEarly,
    (state: RootState) => state.currentSale.status,
    (state: RootState) => state.currentSale.readyToComplete,
  ],
  (isSubmittedSaleEarly, status, readyToComplete) =>
    isConfirmedNotReadyToComplete({
      isSubmittedSaleEarly,
      status,
      readyToComplete,
    })
)

const selectIsSavedUnfulfilled = createSelector(
  [(state: RootState) => state.currentSale.isSavedUnfulfilled],
  isSavedUnfulfilled => isSavedUnfulfilled
)

const selectIsSavedService = createSelector(
  [(state: RootState) => state.currentSale.isSavedService],
  isSavedService => isSavedService
)

const selectHasPayments = createSelector(
  [(state: RootState) => state.currentSale.payments],
  payments => {
    return payments.length > 0
  }
)

const selectIsNoteInvalid = createSelector(
  [
    (state: RootState) => selectNoteIsInvalid(state),
    (state: RootState) => selectFulfilmentNoteIsInvalid(state),
  ],
  (noteIsInvalid, fulfilmentNoteIsInvalid) =>
    Boolean(noteIsInvalid || fulfilmentNoteIsInvalid)
)

export const useShouldDisableActions = () => {
  const saleManager = useSaleManager()
  const trainingModeManager = useTrainingModeManager()
  const hasActiveSale = useAppSelector(selectHasActiveSale)
  const confirmedNotReadyToComplete = useAppSelector(
    selectIsConfirmedNotReadyToComplete
  )
  const isSavedUnfulfilled = useAppSelector(selectIsSavedUnfulfilled)
  const isSavedService = useAppSelector(selectIsSavedService)
  const hasPayments = useAppSelector(selectHasPayments)
  const isFinalisingSale = useAppSelector(selectIsFinalisingSale)
  const hasUnknownLineItems = useAppSelector(selectUnknownLineItems).length > 0
  const returnWithoutPermission = useAppSelector(
    selectIsAReturnWithoutPermission
  )
  const isNoteInvalid = useAppSelector(selectIsNoteInvalid)

  const [trainingModeActive, setTrainingModeActive] = useState(
    trainingModeManager.trainingModeEnabled
  )
  const [isCalculatingSale, setIsCalculatingSale] = useState(false)

  useEffect(() => {
    const setActive = () =>
      setTrainingModeActive(trainingModeManager.trainingModeEnabled)

    trainingModeManager.on('trainingModeEnabledChange', setActive)

    return () => {
      trainingModeManager.off('trainingModeEnabledChange', setActive)
    }
  }, [trainingModeManager])

  useEffect(() => {
    const setStartCalculating = () => setIsCalculatingSale(true)
    const setStopCalculating = () => setIsCalculatingSale(false)

    saleManager.on('saleManager:saleCalculating:start', setStartCalculating)
    saleManager.on('saleManager:saleCalculating:end', setStopCalculating)

    return () => {
      saleManager.off('saleManager:saleCalculating:start', setStartCalculating)
      saleManager.off('saleManager:saleCalculating:end', setStopCalculating)
    }
  }, [saleManager])

  const disable = useMemo(() => {
    if (!hasActiveSale) {
      return true
    }

    if (
      !confirmedNotReadyToComplete &&
      !isSavedUnfulfilled &&
      !isSavedService &&
      hasPayments
    ) {
      return true
    }

    if (trainingModeActive) {
      return true
    }

    if (isFinalisingSale) {
      return true
    }

    if (isCalculatingSale) {
      return true
    }

    if (hasUnknownLineItems) {
      return true
    }

    if (returnWithoutPermission) {
      return true
    }

    if (isNoteInvalid) {
      return true
    }

    return false
  }, [
    confirmedNotReadyToComplete,
    hasActiveSale,
    hasPayments,
    hasUnknownLineItems,
    isCalculatingSale,
    isFinalisingSale,
    isNoteInvalid,
    isSavedService,
    isSavedUnfulfilled,
    returnWithoutPermission,
    trainingModeActive,
  ])

  return disable
}
